import React from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, Chip, Button, Grid, IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import ItemCard from './ItemCard';
import { organizeItemsWithChangeOrders } from '../../utils/projectViewUtils';

const RoomAccordion = ({
  project, filter, bulkActionMode, selectedItems, user, canEdit,
  onToggleItem, onDeleteRoom, onAddItemToRoom, onEditWorkArea, onEditRoom,
  onOpenItemEdit, onApproval, onMarkWorkComplete, onCompletionApproval,
  onOpenChangeOrder, onDeleteItem, onOpenDisburse,
  onOpenComments, onOpenAttachments, onOpenRevisions,
  onOpenDispute, onOpenResolveDispute,
}) => {
  if (!project.rooms || project.rooms.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary">
          No rooms yet. Add your first room to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {project.rooms.map((room) => {
        const activeItems = room.items.filter(item =>
          user.role === 'contractor' || user.role === 'admin' || item.status !== 'pending_homeowner'
        );
        const totalItems = activeItems.length;
        const approvedItems = activeItems.filter(item =>
          item.contractor_approval === 'approved' && item.homeowner_approval === 'approved'
        ).length;
        const outstandingItems = activeItems.filter(item =>
          item.contractor_approval === 'outstanding' || item.homeowner_approval === 'outstanding'
        ).length;

        return (
          <Accordion
            key={room.id}
            id={`room-${room.id}`}
            defaultExpanded
            sx={{
              ...(room.type === 'work_area' && {
                backgroundColor: 'rgba(63, 81, 181, 0.04)',
                border: '2px solid rgba(63, 81, 181, 0.3)',
                '&:before': { display: 'none' },
                mb: 2,
              }),
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                ...(room.type === 'work_area' && {
                  backgroundColor: 'rgba(63, 81, 181, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(63, 81, 181, 0.12)' },
                }),
              }}
            >
              <Box display="flex" flexDirection="column" width="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    {room.type === 'work_area' && <LayersIcon color="primary" sx={{ fontSize: 28 }} />}
                    <Typography
                      variant="h6"
                      sx={{ ...(room.type === 'work_area' && { color: 'primary.main', fontWeight: 600 }) }}
                    >
                      {room.name}
                    </Typography>
                    {(() => {
                      const dims = [];
                      const sqft = parseFloat(room.square_footage);
                      const h = parseFloat(room.ceiling_height);
                      if (Number.isFinite(sqft) && sqft > 0) dims.push(`${sqft} sq ft`);
                      if (Number.isFinite(h) && h > 0) dims.push(`${h} ft ceiling`);
                      return dims.length > 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          · {dims.join(' · ')}
                        </Typography>
                      ) : null;
                    })()}
                  </Box>
                  <Box display="flex" gap={1} mr={2}>
                    <Chip
                      label={`${totalItems} items, ${approvedItems} approved, ${outstandingItems} outstanding`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    {canEdit && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={(e) => { e.stopPropagation(); onAddItemToRoom(room.id); }}
                        sx={{ minWidth: 'auto', px: 1.5 }}
                      >
                        Add Item
                      </Button>
                    )}
                    {canEdit && room.type === 'work_area' && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); onEditWorkArea(room); }}
                        title="Edit work area"
                        aria-label={`Edit work area: ${room.name}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canEdit && room.type === 'room' && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); onEditRoom(room); }}
                        title="Edit room"
                        aria-label={`Edit room: ${room.name}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canEdit && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onDeleteRoom(room.id); }}
                        title="Delete"
                        aria-label={`Delete ${room.type === 'work_area' ? 'work area' : 'room'}: ${room.name}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                {room.type === 'work_area' && room.linked_rooms && room.linked_rooms.length > 0 && (
                  <Box display="flex" gap={0.5} mt={1} flexWrap="wrap" onClick={(e) => e.stopPropagation()}>
                    <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>Rooms:</Typography>
                    {room.linked_rooms.map((linkedRoom) => (
                      <Chip
                        key={linkedRoom.id}
                        label={linkedRoom.name}
                        size="small"
                        variant="outlined"
                        clickable
                        onClick={() => {
                          const el = document.getElementById(`room-${linkedRoom.id}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        sx={{
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                {organizeItemsWithChangeOrders(
                  room.items.filter(item =>
                    user.role === 'contractor' || user.role === 'admin' || item.status !== 'pending_homeowner'
                  ),
                  filter,
                  user.role
                ).map((item) => (
                  <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }} key={item.id}>
                    <ItemCard
                      item={item}
                      room={room}
                      project={project}
                      user={user}
                      canEdit={canEdit}
                      bulkActionMode={bulkActionMode}
                      selectedItems={selectedItems}
                      onToggleItem={onToggleItem}
                      onApproval={onApproval}
                      onMarkWorkComplete={onMarkWorkComplete}
                      onCompletionApproval={onCompletionApproval}
                      onOpenChangeOrder={onOpenChangeOrder}
                      onDeleteItem={onDeleteItem}
                      onOpenDisburse={onOpenDisburse}
                      onOpenComments={onOpenComments}
                      onOpenAttachments={onOpenAttachments}
                      onOpenRevisions={onOpenRevisions}
                      onOpenDispute={onOpenDispute}
                      onOpenResolveDispute={onOpenResolveDispute}
                      onOpenItemEdit={onOpenItemEdit}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </>
  );
};

export default RoomAccordion;
